import { createContext, useContext } from "react";
import { ReactNode } from "react";
import { DEFAULT_ROTATION } from "../canvas/config";
import { ExportError } from "../../../../../types";
import {
  Background,
  Content,
  Position,
  Rotation,
  Size,
  SolidConfiguration,
  Stroke,
  TextConfiguration,
  VideoConfiguration,
} from "../../types";

type BaseVisualSettings = {
  position: Position;
  rotation: Rotation;
  size: Size;
};

export type TextItemSettings = BaseVisualSettings & {
  type: "text";
  content: Content;
  configuration: TextConfiguration;
};

export type SolidItemSettings = BaseVisualSettings & {
  type: "solid";
  configuration: SolidConfiguration;
};

export type ImageItemSettings = BaseVisualSettings & {
  type: "image";
};

export type VideoItemSettings = {
  type: "video";
  position: Position;
  rotation: Rotation;
  configuration: VideoConfiguration;
};

export type ItemSettings =
  | TextItemSettings
  | SolidItemSettings
  | ImageItemSettings
  | VideoItemSettings;

export type SettingsContextType = {
  isEditorLoaded: boolean;
  loadEditor: () => void;
  applyToAll: boolean;
  updateContentSettings: (content: Partial<Content>) => void;
  updatePositionSettings: (position: Partial<Position>) => void;
  updateStrokeSettings: (stroke: Partial<Stroke>) => void;
  updateRotationSettings: (rotation: Partial<typeof DEFAULT_ROTATION>) => void;
  updateBackgroundSettings: (background: Partial<Background>) => void;
  updateAlignmentSettings: (
    alignment: Partial<{
      horizontal: string;
      vertical: string;
    }>
  ) => void;
  applyToAllMap: Record<string, boolean>;
  setApplyToAllForCurrentItem: (value: boolean) => void;
  updateFontSettings: (
    fontSettings: Partial<{
      family: string;
      size: number;
      decoration: string;
      lineHeight: number;
      variant: string;
      weight: string;
      color: string;
    }>
  ) => void;
  updateSizeSettings: (
    size: Partial<{
      width: number;
      height: number;
    }>
  ) => void;
  getSelectedItemSettings: () => ItemSettings | null;
  selectedItemId: string | null;
  fonts: any[];
  bottomPanelHeight: number;
  setBottomPanelHeight: (height: number) => void;
  generateVideo: () => Promise<void>;
  displayError: ExportError | null;
  isGenerating: boolean;
  setIsGenerating: (value: boolean) => void;
  generationProgress: number;
  videoDownloadUrl: string | undefined;
  setFilterValue: (filterType: string, newValue: number) => void;
  addTextItem: (options: { startTimeInFrames: number }) => void;
};

export interface SettingsProviderProps {
  children: ReactNode;
  fonts: any[];
}

export const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
